import React, { useEffect, useState } from 'react';

import { motion } from 'framer-motion';
import { Github, Linkedin, Mail } from 'lucide-react';

export function TeamMember({ index, member, teamImages, XIcon, BOSSS_BLUE }) {

  const [finalSrc, setFinalSrc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const resolveImage = () => {
      const remoteUrl = member.image;
      const localFallback = teamImages[member.last_name.replace('-', '_')]?.src;

      if (!remoteUrl) {
        setFinalSrc(localFallback);
        setLoading(false);
        return;
      }

      const img = new Image();
      img.src = remoteUrl;

      img.onload = () => {
        setFinalSrc(remoteUrl);
        setLoading(false);
      };

      img.onerror = () => {
        setFinalSrc(localFallback);
        setLoading(false);
      };
    };

    resolveImage();
  }, [member.image, member.last_name, teamImages]);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      key={`${index}-${member.name}`}
      initial={{ opacity: 0, y: 40 }}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300"
    >
      <div className="p-8 flex flex-col sm:flex-row gap-6">
        {/* Profile Image */}
        <div className="flex-shrink-0">
          <div
            className="w-32 h-32 rounded-xl overflow-hidden mx-auto sm:mx-0 flex items-center justify-center bg-gray-50"
            style={{
              boxShadow: `0 4px 20px ${BOSSS_BLUE}20`,
              border: !member.image ? `1px solid ${BOSSS_BLUE}20` : 'none',
            }}
          >
            {finalSrc
              ? (
                <img
                  src={finalSrc}
                  className="w-full h-full object-cover"

                  alt={`${member.first_name} ${member.last_name}`}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
              )
              : null}

            {/* SVG Template Placeholder (shows if image is null OR fails to load) */}
            <svg
              fill="none"
              strokeWidth="1.5"
              viewBox="0 0 24 24"
              stroke={BOSSS_BLUE}
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ opacity: 0.6 }}
              className={`w-16 h-16 ${finalSrc ? 'hidden' : 'block'}`}
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle r="4" cy="7" cx="12" />
            </svg>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 text-center sm:text-left">
          <h3
            style={{ color: '#1e293b' }}
            className="text-xl font-bold mb-1"
          >
            {member.first_name}
            {' '}
            {member.last_name}
          </h3>

          <p
            style={{ color: BOSSS_BLUE }}
            className="text-sm font-medium mb-3"
          >
            {member.position}
          </p>

          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            {member.description}
          </p>

          {/* Social Links */}
          <div className="flex gap-3 justify-center sm:justify-start">
            {member.socials?.map((opt, optIndex) => (
              <React.Fragment key={`${index}-${optIndex}-${opt.name}`}>
                {
                  opt.name === 'Email' && (
                    <a
                      title="Email"
                      href={`mailto:${opt.url}`}
                      className="w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
                      style={{
                        backgroundColor: `${BOSSS_BLUE}10`,
                        color: BOSSS_BLUE,
                      }}
                    >
                      <Mail className="w-4 h-4" />
                    </a>
                  )
                }

                {
                  opt.name === 'LinkedIn' && (
                    <a
                      href={opt.url}
                      target="_blank"
                      title="LinkedIn"
                      rel="noopener noreferrer"
                      className="w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
                      style={{
                        backgroundColor: `${BOSSS_BLUE}10`,
                        color: BOSSS_BLUE,
                      }}
                    >
                      <Linkedin className="w-4 h-4" />
                    </a>
                  )
                }


                {
                  opt.name === 'X' && (
                    <a
                      href={opt.url}
                      target="_blank"
                      title="X (Twitter)"
                      rel="noopener noreferrer"
                      className="w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
                      style={{
                        backgroundColor: `${BOSSS_BLUE}10`,
                        color: BOSSS_BLUE,
                      }}
                    >
                      <XIcon className="w-4 h-4" />
                    </a>
                  )
                }

                {
                  opt.name === 'GitHub' && (
                    <a
                      href={opt.url}
                      title="GitHub"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
                      style={{
                        backgroundColor: `${BOSSS_BLUE}10`,
                        color: BOSSS_BLUE,
                      }}
                    >
                      <Github className="w-4 h-4" />
                    </a>
                  )
                }
              </React.Fragment>
            ))}

          </div>
        </div>
      </div>
    </motion.div>
  );
}
